import { useEffect, useMemo, useRef, useState } from 'react';
import { fmtMoney, fmtNum, getLang, localeOf, useT } from '../lib/i18n';
import { haptic } from '../lib/telegram';

/**
 * The month, as a month.
 *
 * A bar chart answers "is this month better than last". A calendar
 * answers "which days actually make money" — and for a coach whose week
 * splits into mornings and evenings, that's the more useful question.
 *
 * Past days show NET: what's left after court hire, in green. Future days
 * show the same figure in grey, because a projection is not money. The
 * colour difference is doing real work — it's the only thing stopping a
 * good-looking week ahead from reading as a good week had.
 */

interface Day {
  date: string;
  earned: number;
  collected: number;
  court: number;
  net: number;
  sessions: number;
  projected: number;
  projectedCourt: number;
  projectedNet: number;
  projectedSessions: number;
}

/** Monday-first initials, from the language rather than hardcoded. */
const weekdayInitials = () => {
  const base = new Date(2024, 0, 1); // a Monday
  return Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(localeOf(getLang()), { weekday: 'narrow' })
      .format(new Date(base.getFullYear(), base.getMonth(), base.getDate() + i)));
};
const pad = (n: number) => String(n).padStart(2, '0');

/** A point tapped on the Money chart: its day, week (from Monday) or month. */
export interface CalFocus { date: string; span: 'day' | 'week' | 'month'; seq: number }

const mondayKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 6) % 7));
  return dt.toISOString().slice(0, 10);
};

export function MonthCalendar({
  days,
  courtRate,
  onPickDay,
  focus,
}: {
  days: Day[];
  courtRate: number;
  onPickDay: (date: string, day: Day | undefined) => void;
  focus?: CalFocus | null;
}) {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const [lit, setLit] = useState<CalFocus | null>(null);
  const root = useRef<HTMLDivElement>(null);

  // A tap on the chart: open that month, light the day, week or month,
  // and bring it into view.
  useEffect(() => {
    if (!focus) return;
    const now = new Date();
    const [y, m] = focus.date.split('-').map(Number);
    setOffset((y - now.getFullYear()) * 12 + (m - 1 - now.getMonth()));
    setLit(focus);
    const timer = setTimeout(() => setLit(null), 1800);
    return () => clearTimeout(timer);
  }, [focus?.seq]);

  useEffect(() => {
    if (!lit) return;
    const el = root.current?.parentElement?.querySelector('.cal__cell--focus') ?? root.current;
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [lit?.seq]);

  const isLit = (key: string) => !!lit && (
    lit.span === 'day' ? key === lit.date
      : lit.span === 'week' ? mondayKey(key) === lit.date
        : key.slice(0, 7) === lit.date.slice(0, 7));

  const { cells, label, totals } = useMemo(() => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const year = first.getFullYear();
    const month = first.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Monday-first, so the week reads like a working week.
    const leading = (first.getDay() + 6) % 7;

    const byDate = new Map(days.map((d) => [d.date, d]));
    const todayKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

    const cells: ({ day: number; key: string; data?: Day; isToday: boolean; future: boolean } | null)[] = [];
    for (let i = 0; i < leading; i++) cells.push(null);

    let earned = 0, court = 0, sessions = 0;
    let projected = 0, projectedCourt = 0, projectedSessions = 0;

    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${pad(month + 1)}-${pad(d)}`;
      const data = byDate.get(key);
      if (data) {
        earned += data.earned;
        court += data.court;
        sessions += data.sessions;
        projected += data.projected;
        projectedCourt += data.projectedCourt;
        projectedSessions += data.projectedSessions;
      }
      cells.push({ day: d, key, data, isToday: key === todayKey, future: key > todayKey });
    }

    return {
      cells,
      label: new Intl.DateTimeFormat(localeOf(getLang()), { month: 'long', year: 'numeric' }).format(first),
      totals: {
        earned: Math.round(earned),
        court: Math.round(court),
        net: Math.round(earned - court),
        sessions,
        projected: Math.round(projected),
        projectedCourt: Math.round(projectedCourt),
        projectedNet: Math.round(projected - projectedCourt),
        projectedSessions,
      },
    };
  }, [days, offset]);

  const peak = Math.max(1, ...cells.map((c) => (c && !c.future ? c.data?.net ?? 0 : 0)));

  return (
    <>
      <div className="monthnav" ref={root}>
        <button onClick={() => { haptic.tap(); setOffset(offset - 1); }}>‹</button>
        <span>{label}</span>
        <button onClick={() => { haptic.tap(); setOffset(offset + 1); }}>›</button>
      </div>

      <div className="cal">
        {weekdayInitials().map((d, i) => <div className="cal__head" key={i}>{d}</div>)}
        {cells.map((c, i) => {
          if (c === null) return <div className="cal__cell cal__cell--empty" key={`e${i}`} />;
          const hasAny = c.data && (c.data.sessions > 0 || c.data.projectedSessions > 0);
          const amount = c.future ? c.data?.projectedNet ?? 0 : c.data?.net ?? 0;
          // Past days are tinted by what they made, so the good days show
          // at a glance. Future days stay flat: a projection is not money.
          const heat = !c.future && amount > 0 ? Math.round(12 + 38 * Math.min(1, amount / peak)) : 0;
          return (
            <button
              className={`cal__cell${c.isToday ? ' cal__cell--today' : ''}${hasAny ? '' : ' cal__cell--quiet'}${isLit(c.key) ? ' cal__cell--focus' : ''}`}
              style={heat ? { background: `color-mix(in srgb, var(--court) ${heat}%, var(--surface))` } : undefined}
              key={c.key}
              onClick={() => { haptic.tap(); onPickDay(c.key, c.data); }}
            >
              <span className="cal__day">{c.day}</span>
              {hasAny && (
                <span className={
                  c.future ? 'cal__amount cal__amount--future'
                    : amount < 0 ? 'cal__amount cal__amount--neg' : 'cal__amount'
                }>
                  {amount < 0 ? '−' : ''}{fmtNum(Math.abs(Math.round(amount)))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="totals">
        <div className="totals__row">
          <span>{t('co.cal.earned', { n: totals.sessions, sessions: t.word('co.today.sessions', totals.sessions) })}</span>
          <b>{fmtMoney(totals.earned)}</b>
        </div>
        {courtRate > 0 && (
          <div className="totals__row totals__row--cost">
            <span>{t('co.cal.courtHire')}</span>
            <b>−{fmtMoney(totals.court)}</b>
          </div>
        )}
        <div className="totals__row totals__row--net">
          <span>{t('co.cal.yours')}</span>
          <b>{fmtMoney(totals.net)}</b>
        </div>

        {totals.projectedSessions > 0 && (
          <div className="totals__projected">
            <div className="totals__row">
              <span>{t('co.cal.stillToCome', { n: totals.projectedSessions })}</span>
              <b>{fmtMoney(totals.projected)}</b>
            </div>
            {courtRate > 0 && (
              <div className="totals__row totals__row--cost">
                <span>{t('co.cal.courtToCome')}</span>
                <b>−{fmtMoney(totals.projectedCourt)}</b>
              </div>
            )}
            <div className="totals__row">
              <span>{t('co.cal.ifAllTurnUp')}</span>
              <b>{fmtMoney(totals.net + totals.projectedNet)}</b>
            </div>
          </div>
        )}
      </div>

      {courtRate === 0 && (
        <div className="card__when" style={{ padding: '4px 16px 12px' }}>
          {t('co.month.setCourtRate')}
        </div>
      )}
    </>
  );
}
